import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBusiness } from '../../contexts/BusinessContext';
import api from '../../services/api';
import { 
  CreditCard, CheckCircle, AlertCircle, Calendar, TrendingUp, 
  Users, Package, MapPin, FileText, Zap, Clock, ChevronRight,
  Receipt, Star, Shield, Activity, Sparkles, ArrowRight,
  Building, HardDrive, BarChart3, Crown, RefreshCw
} from 'lucide-react';
import { toast } from 'react-hot-toast';

const BillingDashboard = () => {
  const navigate = useNavigate();
  const { activeBusiness } = useBusiness();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [usage, setUsage] = useState({
    staff: { used: 0, limit: 5 },
    invoices: { used: 0, limit: 100 },
    storage: { used: 0, limit: 10 },
    retailers: { used: 0, limit: 50 }
  });

  const bid = activeBusiness?.id;

  useEffect(() => {
    if (bid) {
      fetchBillingData();
    }
  }, [bid]);

  const fetchBillingData = async () => {
    try {
      setLoading(true);

      const [subRes, entRes, invRes] = await Promise.all([
        api.get('/business/subscription').catch(() => null),
        api.get('/business/entitlements').catch(() => null),
        api.get('/billing/invoices').catch(() => null),
      ]);

      if (subRes?.data?.subscription) {
        const s = subRes.data.subscription;
        setSubscription({
          plan_name: s.plan?.name || 'Basic Plan',
          plan_code: s.plan?.code?.toUpperCase() || 'BASIC',
          status: (s.status || 'active').toUpperCase(),
          next_renewal_date: s.effective_to || null,
          billing_cycle: 'Monthly',
          cancel_at_period_end: false,
        });
      }

      if (entRes?.data?.entitlements) {
        const lim = entRes.data.entitlements.limits || {};
        setUsage({
          staff:     { used: 0, limit: lim.max_staff || lim.max_staff_total || 5 },
          invoices:  { used: 0, limit: lim.sales_invoices || 100 },
          storage:   { used: 0, limit: 10 },
          retailers: { used: 0, limit: lim.max_customers || 50 },
        });
      }

      if (invRes?.data) {
        setRecentInvoices(Array.isArray(invRes.data) ? invRes.data.slice(0, 5) : []);
      }
    } catch (error) {
      console.error('Error fetching billing data:', error);
      toast.error('Failed to load billing information');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getDaysUntilRenewal = () => {
    if (!subscription?.next_renewal_date) return null;
    const diff = new Date(subscription.next_renewal_date) - new Date();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  const getUsagePercentage = (used, limit) => {
    if (!limit || limit === '∞') return 0;
    return Math.min((used / limit) * 100, 100);
  };

  const getUsageColor = (percentage) => {
    if (percentage >= 90) return { bar: 'bg-red-500', bg: 'bg-red-100', text: 'text-red-600' };
    if (percentage >= 70) return { bar: 'bg-amber-500', bg: 'bg-amber-100', text: 'text-amber-600' };
    return { bar: 'bg-emerald-500', bg: 'bg-emerald-100', text: 'text-emerald-600' };
  };

  const daysUntilRenewal = getDaysUntilRenewal();

  // Plan tier styling
  const getPlanStyle = (planName) => {
    const name = (planName || '').toLowerCase();
    if (name.includes('enterprise') || name.includes('platinum')) {
      return { gradient: 'from-purple-600 to-indigo-600', icon: Crown, badge: 'bg-purple-100 text-purple-700' };
    }
    if (name.includes('growth') || name.includes('pro')) {
      return { gradient: 'from-blue-600 to-cyan-600', icon: Sparkles, badge: 'bg-blue-100 text-blue-700' };
    }
    if (name.includes('starter') || name.includes('basic')) {
      return { gradient: 'from-teal-600 to-emerald-600', icon: Zap, badge: 'bg-teal-100 text-teal-700' };
    }
    return { gradient: 'from-slate-600 to-slate-700', icon: Shield, badge: 'bg-slate-100 text-slate-700' };
  };

  const planStyle = getPlanStyle(subscription?.plan_name);
  const PlanIcon = planStyle.icon;

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-[#F8F9FB]">
        <div className="text-center">
          <div className="w-12 h-12 border-[3px] border-slate-200 border-t-teal-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[14px] text-slate-500">Loading billing information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-[#F8F9FB] animate-fadeIn">
      {/* ════════════════════════════════════════════════════════════════════════
          PREMIUM PAGE HEADER
          ════════════════════════════════════════════════════════════════════════ */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-[1600px] mx-auto px-6 py-5">
          {/* Title Row */}
          <div className="flex items-center justify-between mb-5">
            <div>
              <h1 className="text-[22px] font-bold text-slate-800 tracking-tight">Billing & Subscription</h1>
              <p className="text-[13px] text-slate-500 mt-0.5">Manage your subscription, usage limits and payment history</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(`/business/${bid}/billing/invoices`)}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 hover:border-slate-300 transition-all duration-200 text-[13px] font-medium shadow-sm active:scale-[0.98]"
              >
                <Receipt className="w-4 h-4" />
                View Invoices
              </button>
              <button
                onClick={() => navigate(`/business/${bid}/billing/plans`)}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-teal-600 to-emerald-600 text-white rounded-xl hover:from-teal-700 hover:to-emerald-700 transition-all duration-200 text-[13px] font-semibold shadow-lg shadow-teal-600/25 active:scale-[0.98]"
              >
                <TrendingUp className="w-4 h-4" />
                Upgrade Plan
              </button>
            </div>
          </div>

          {/* Stats Pills */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="inline-flex items-center gap-2.5 px-4 py-2.5 bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl border border-slate-200/60">
              <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${planStyle.gradient} flex items-center justify-center shadow-sm`}>
                <PlanIcon className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">Current Plan</p>
                <p className="text-[14px] font-bold text-slate-800">{subscription?.plan_name || 'Free'}</p>
              </div>
            </div>

            <div className="inline-flex items-center gap-2.5 px-4 py-2.5 bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl border border-slate-200/60">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                subscription?.status === 'ACTIVE' ? 'bg-emerald-100' : 'bg-amber-100'
              }`}>
                {subscription?.status === 'ACTIVE' ? (
                  <CheckCircle className="w-4 h-4 text-emerald-600" />
                ) : (
                  <Clock className="w-4 h-4 text-amber-600" />
                )}
              </div>
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">Status</p>
                <p className={`text-[14px] font-bold ${
                  subscription?.status === 'ACTIVE' ? 'text-emerald-600' : 'text-amber-600'
                }`}>{subscription?.status || 'Inactive'}</p>
              </div>
            </div>

            <div className="inline-flex items-center gap-2.5 px-4 py-2.5 bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl border border-slate-200/60">
              <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                <Calendar className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">Next Renewal</p>
                <p className="text-[14px] font-bold text-slate-800">{formatDate(subscription?.next_renewal_date)}</p>
              </div>
            </div>

            {daysUntilRenewal !== null && daysUntilRenewal <= 7 && (
              <div className="inline-flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-xl animate-pulse">
                <AlertCircle className="w-4 h-4 text-amber-600" />
                <span className="text-[12px] font-semibold text-amber-700">
                  {daysUntilRenewal <= 0 ? 'Renewal due!' : `${daysUntilRenewal} days left`}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ════════════════════════════════════════════════════════════════════════
          MAIN CONTENT
          ════════════════════════════════════════════════════════════════════════ */}
      <div className="flex-1 overflow-y-auto px-6 py-5">
        <div className="max-w-[1600px] mx-auto">
          
          {/* Cancellation Warning */}
          {subscription?.cancel_at_period_end && (
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-2xl px-5 py-4 flex items-center gap-4 mb-5 animate-slideDown">
              <div className="w-11 h-11 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-6 h-6 text-amber-600" />
              </div>
              <div className="flex-1">
                <p className="text-[14px] font-semibold text-amber-800">Subscription Ending Soon</p>
                <p className="text-[13px] text-amber-700">
                  Your subscription will be canceled on {formatDate(subscription.next_renewal_date)}. You can continue using all features until then.
                </p>
              </div>
              <button 
                onClick={() => navigate(`/business/${bid}/billing/plans`)}
                className="px-4 py-2 bg-amber-600 text-white rounded-xl text-[13px] font-medium hover:bg-amber-700 transition-colors"
              >
                Renew Now
              </button>
            </div>
          )}

          <div className="grid grid-cols-12 gap-5">
            
            {/* ══════════════════════════════════════════════════════════════════
                LEFT COLUMN - Plan Card & Usage
                ══════════════════════════════════════════════════════════════════ */}
            <div className="col-span-12 lg:col-span-8 space-y-5">
              
              {/* Premium Plan Card */}
              <div className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${planStyle.gradient} p-6 shadow-xl`}>
                {/* Decorative Elements */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
                <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2"></div>
                
                <div className="relative z-10">
                  <div className="flex items-start justify-between mb-6">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center">
                          <PlanIcon className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <p className="text-white/70 text-[12px] uppercase tracking-wider font-medium">Your Plan</p>
                          <h2 className="text-[24px] font-bold text-white">{subscription?.plan_name || 'Free Plan'}</h2>
                        </div>
                      </div>
                    </div>
                    <span className={`px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wide ${
                      subscription?.status === 'ACTIVE' 
                        ? 'bg-white/20 text-white backdrop-blur' 
                        : 'bg-amber-400 text-amber-900'
                    }`}>
                      {subscription?.status || 'INACTIVE'}
                    </span>
                  </div>

                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="bg-white/10 backdrop-blur rounded-xl p-4">
                      <p className="text-white/70 text-[11px] uppercase tracking-wide mb-1">Billing Cycle</p>
                      <p className="text-white font-semibold text-[15px]">{subscription?.billing_cycle || 'Monthly'}</p>
                    </div>
                    <div className="bg-white/10 backdrop-blur rounded-xl p-4">
                      <p className="text-white/70 text-[11px] uppercase tracking-wide mb-1">Amount</p>
                      <p className="text-white font-semibold text-[15px]">₹{subscription?.amount || 0}/mo</p>
                    </div>
                    <div className="bg-white/10 backdrop-blur rounded-xl p-4">
                      <p className="text-white/70 text-[11px] uppercase tracking-wide mb-1">Renewal</p>
                      <p className="text-white font-semibold text-[15px]">{formatDate(subscription?.next_renewal_date)}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => navigate(`/business/${bid}/billing/plans`)}
                      className="flex-1 flex items-center justify-center gap-2 px-5 py-3 bg-white text-slate-800 rounded-xl font-semibold text-[14px] hover:bg-slate-50 transition-all duration-200 active:scale-[0.98] shadow-lg"
                    >
                      <Sparkles className="w-4 h-4" />
                      Upgrade Plan
                    </button>
                    <button
                      onClick={fetchBillingData}
                      className="w-12 h-12 flex items-center justify-center bg-white/20 backdrop-blur rounded-xl hover:bg-white/30 transition-colors"
                    >
                      <RefreshCw className="w-5 h-5 text-white" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Usage Meters Card */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-sm">
                      <BarChart3 className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-[15px] font-semibold text-slate-800">Usage & Limits</h3>
                      <p className="text-[12px] text-slate-500">Current usage for {subscription?.plan_name || 'your plan'}</p>
                    </div>
                  </div>
                </div>

                <div className="p-5 grid grid-cols-2 gap-5">
                  {/* Staff Members */}
                  <UsageMeter
                    icon={Users}
                    label="Staff Members"
                    used={usage.staff.used}
                    limit={usage.staff.limit}
                    unit="members"
                  />

                  {/* Invoices This Month */}
                  <UsageMeter
                    icon={FileText}
                    label="Invoices This Month"
                    used={usage.invoices.used}
                    limit={usage.invoices.limit}
                    unit="invoices"
                  />

                  {/* Storage */}
                  <UsageMeter
                    icon={HardDrive}
                    label="Storage Used"
                    used={usage.storage.used}
                    limit={usage.storage.limit}
                    unit="GB"
                    decimals={1}
                  />

                  {/* Retailers */}
                  <UsageMeter
                    icon={Building}
                    label="Retailers"
                    used={usage.retailers.used}
                    limit={usage.retailers.limit}
                    unit="retailers"
                  />
                </div>

                {/* Upgrade prompt if usage is high */}
                {(getUsagePercentage(usage.staff.used, usage.staff.limit) >= 80 ||
                  getUsagePercentage(usage.invoices.used, usage.invoices.limit) >= 80) && (
                  <div className="px-5 pb-5">
                    <div className="bg-gradient-to-r from-teal-50 to-emerald-50 border border-teal-200 rounded-xl p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
                          <TrendingUp className="w-5 h-5 text-teal-600" />
                        </div>
                        <div>
                          <p className="text-[13px] font-semibold text-teal-800">Running low on limits?</p>
                          <p className="text-[12px] text-teal-600">Upgrade to unlock more features and higher limits</p>
                        </div>
                      </div>
                      <button
                        onClick={() => navigate(`/business/${bid}/billing/plans`)}
                        className="px-4 py-2 bg-teal-600 text-white rounded-lg text-[13px] font-medium hover:bg-teal-700 transition-colors flex items-center gap-2"
                      >
                        Upgrade
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* ══════════════════════════════════════════════════════════════════
                RIGHT COLUMN - Recent Invoices & Quick Actions
                ══════════════════════════════════════════════════════════════════ */}
            <div className="col-span-12 lg:col-span-4 space-y-5">
              
              {/* Recent Invoices Card */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center shadow-sm">
                      <Receipt className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-[15px] font-semibold text-slate-800">Recent Invoices</h3>
                      <p className="text-[12px] text-slate-500">{recentInvoices.length} invoice{recentInvoices.length !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => navigate(`/business/${bid}/billing/invoices`)}
                    className="text-[12px] text-teal-600 hover:text-teal-700 font-medium flex items-center gap-1"
                  >
                    View All
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>

                <div className="divide-y divide-slate-100">
                  {recentInvoices.length === 0 ? (
                    <div className="text-center py-10">
                      <div className="w-14 h-14 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <Receipt className="w-7 h-7 text-slate-400" />
                      </div>
                      <p className="text-[13px] text-slate-500">No invoices yet</p>
                    </div>
                  ) : (
                    recentInvoices.map((invoice, index) => (
                      <div 
                        key={invoice.id} 
                        className="px-5 py-3.5 hover:bg-slate-50 transition-colors cursor-pointer"
                        onClick={() => navigate(`/business/${bid}/billing/invoices`)}
                        style={{ animationDelay: `${index * 50}ms` }}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-9 h-9 rounded-full flex items-center justify-center ${
                              invoice.status === 'PAID' ? 'bg-emerald-100' : 
                              invoice.status === 'PENDING' ? 'bg-amber-100' : 'bg-red-100'
                            }`}>
                              {invoice.status === 'PAID' ? (
                                <CheckCircle className="w-4 h-4 text-emerald-600" />
                              ) : (
                                <Clock className="w-4 h-4 text-amber-600" />
                              )}
                            </div>
                            <div>
                              <p className="text-[13px] font-semibold text-slate-800">{invoice.invoice_no}</p>
                              <p className="text-[11px] text-slate-500">{formatDate(invoice.created_at)}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-[13px] font-semibold text-slate-800">₹{invoice.amount_due?.toLocaleString() || 0}</p>
                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${
                              invoice.status === 'PAID' ? 'bg-emerald-100 text-emerald-700' :
                              invoice.status === 'PENDING' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                            }`}>
                              {invoice.status === 'PAID' ? 'Paid' : invoice.status === 'PENDING' ? 'Due' : 'Overdue'}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Quick Actions Card */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5">
                <h3 className="text-[14px] font-semibold text-slate-800 mb-4">Quick Actions</h3>
                <div className="space-y-2">
                  <button
                    onClick={() => navigate(`/business/${bid}/billing/plans`)}
                    className="w-full flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-teal-50 to-emerald-50 hover:from-teal-100 hover:to-emerald-100 border border-teal-200 rounded-xl transition-all duration-200 text-left group"
                  >
                    <div className="w-9 h-9 rounded-lg bg-teal-100 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <TrendingUp className="w-4 h-4 text-teal-600" />
                    </div>
                    <div>
                      <p className="text-[13px] font-semibold text-teal-800">Upgrade Plan</p>
                      <p className="text-[11px] text-teal-600">Get more features & limits</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-teal-400 ml-auto" />
                  </button>

                  <button
                    onClick={() => navigate(`/business/${bid}/billing/invoices`)}
                    className="w-full flex items-center gap-3 px-4 py-3 bg-slate-50 hover:bg-slate-100 rounded-xl transition-all duration-200 text-left group"
                  >
                    <div className="w-9 h-9 rounded-lg bg-slate-200 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <FileText className="w-4 h-4 text-slate-600" />
                    </div>
                    <div>
                      <p className="text-[13px] font-semibold text-slate-700">View All Invoices</p>
                      <p className="text-[11px] text-slate-500">Download & manage invoices</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-400 ml-auto" />
                  </button>

                  <button
                    onClick={() => toast.success('Payment methods coming soon!')}
                    className="w-full flex items-center gap-3 px-4 py-3 bg-slate-50 hover:bg-slate-100 rounded-xl transition-all duration-200 text-left group"
                  >
                    <div className="w-9 h-9 rounded-lg bg-slate-200 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <CreditCard className="w-4 h-4 text-slate-600" />
                    </div>
                    <div>
                      <p className="text-[13px] font-semibold text-slate-700">Payment Methods</p>
                      <p className="text-[11px] text-slate-500">Manage cards & UPI</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-400 ml-auto" />
                  </button>
                </div>
              </div>

              {/* Help Card */}
              <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-5 text-white">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                    <Activity className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-[14px] font-semibold">Need Help?</h3>
                    <p className="text-[12px] text-slate-400">Contact our support team</p>
                  </div>
                </div>
                <p className="text-[12px] text-slate-400 mb-4">
                  Get assistance with billing, subscriptions, or any account-related queries.
                </p>
                <button className="w-full py-2.5 bg-white/10 hover:bg-white/20 rounded-xl text-[13px] font-medium transition-colors">
                  Contact Support
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
        .animate-slideDown {
          animation: slideDown 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════════
// USAGE METER COMPONENT
// ════════════════════════════════════════════════════════════════════════════════
const UsageMeter = ({ icon: Icon, label, used, limit, unit, decimals = 0 }) => {
  const percentage = limit === '∞' ? 0 : Math.min((used / limit) * 100, 100);
  
  const getColor = () => {
    if (percentage >= 90) return { bar: 'bg-red-500', bg: 'bg-red-50', text: 'text-red-600', ring: 'ring-red-200' };
    if (percentage >= 70) return { bar: 'bg-amber-500', bg: 'bg-amber-50', text: 'text-amber-600', ring: 'ring-amber-200' };
    return { bar: 'bg-emerald-500', bg: 'bg-emerald-50', text: 'text-emerald-600', ring: 'ring-emerald-200' };
  };

  const color = getColor();

  return (
    <div className={`p-4 rounded-xl ${color.bg} ring-1 ${color.ring}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon className={`w-4 h-4 ${color.text}`} />
          <span className="text-[13px] font-medium text-slate-700">{label}</span>
        </div>
        <span className={`text-[12px] font-semibold ${color.text}`}>
          {percentage.toFixed(0)}%
        </span>
      </div>
      
      {/* Progress Bar */}
      <div className="h-2 bg-white rounded-full overflow-hidden mb-2">
        <div 
          className={`h-full ${color.bar} rounded-full transition-all duration-500 ease-out`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      
      <div className="flex items-center justify-between">
        <span className="text-[11px] text-slate-500">
          {decimals > 0 ? used.toFixed(decimals) : used} {unit} used
        </span>
        <span className="text-[11px] font-medium text-slate-600">
          {limit === '∞' ? 'Unlimited' : `${limit} ${unit}`}
        </span>
      </div>
    </div>
  );
};

export default BillingDashboard;
